import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import dayjs from '@/lib/dayjs';
import type { Dayjs } from 'dayjs';
import { PERIOD_STATUS } from '@/models/references/references';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';
import PeriodSummaryModel from '@/models/entities/PeriodSummaryModel';

/** URN pour écrire une relation vers cette ressource (corps d'un POST / PATCH) ; un filtre prend l'id */
export const periodUrn = (periodId: id | number) => urnOf('period', periodId);

/**
 * Période déclarative d'une société (GET /periods, lecture seule) : un mois, Ouverte puis Clôturée (POST
 * /period_status_histories, par un Dirigeant). periodStatus arrive avec son id.
 * timeSheetCount (« Demandes » : feuilles soumises) et totalMinutes (leurs heures) : lus dans son résumé (PeriodSummary,
 * ressource à part : fetchSummaries(periods, PERIOD_SUMMARIES)), 0 tant qu'il n'est pas chargé.
 * Filtres : company, periodStatus, month[after] / month[strictly_before] (une année) ; tri order[month].
 */
export default class PeriodModel extends AbstractApiModel {
  protected _path = '/periods/{?id}';

  declare _tsFilters: {
    company?: string;
    periodStatus?: id;
    'month[after]'?: string;
    'month[strictly_before]'?: string;
  };
  declare _tsSorts: { month?: unknown };

  /** Rempli par fetchSummaries (le résumé n'arrive pas avec la période) */
  @doc.model(() => PeriodSummaryModel) declare periodSummary: PeriodSummaryModel;

  get timeSheetCount(): number {
    return this.periodSummary.timeSheetCount;
  }

  get totalMinutes(): number {
    return this.periodSummary.totalMinutes;
  }

  /**
   * Le 1er du mois. Lu sur la partie date seule (« 2026-09-01T00:00:00+00:00 ») : converti dans le fuseau du navigateur,
   * minuit UTC tomberait la veille (le mois précédent) à l'ouest de Greenwich.
   */
  get monthStart(): Dayjs {
    return dayjs(this.get<string, string>('month', '').slice(0, 10));
  }

  /** « Septembre 2026 » */
  get monthLabel(): string {
    const label = this.monthStart.format('MMMM YYYY');
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  get companyId(): number {
    return Number(this.get<string, string>('company.@id', '').split('/').pop()) || 0;
  }

  /** OPEN / CLOSED */
  get periodStatusReference(): string {
    return referenceOf('periodStatuses', this.get<unknown, unknown>('periodStatus', null))?.reference ?? '';
  }

  get periodStatusLabel(): string {
    return referenceOf('periodStatuses', this.get<unknown, unknown>('periodStatus', null))?.label ?? '';
  }

  get isClosed(): boolean {
    return this.periodStatusReference === PERIOD_STATUS.CLOSED;
  }
}
